// components/districts/DistrictsTable.tsx
import React from 'react';
import {
  RefreshCw,
  Eye,
  ArrowUpDown,
  MapPin,
  AlertTriangle,
  Map
} from 'lucide-react';
import { DistrictsTableProps } from '../../types/districts.types';
import { 
  formatCurrency, 
  formatNumber, 
  formatPercentage, 
  getPerformanceRating, 
  getRiskLevel 
} from '../../utils/districts.utils';

const DistrictsTable: React.FC<DistrictsTableProps> = ({
  districts,
  loading,
  onDistrictClick,
  sortBy,
  sortOrder,
  onSort
}) => {
  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 opacity-40" />;
    return sortOrder === 'ASC' ? (
      <ArrowUpDown className="w-4 h-4 text-blue-600" />
    ) : (
      <ArrowUpDown className="w-4 h-4 text-blue-600 rotate-180" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading districts...</p>
        </div>
      </div>
    );
  }

  if (districts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-12 text-center">
          <Map className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Districts Found</h3>
          <p className="text-gray-600">No districts match your current filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>District</span>
                  {getSortIcon('district_name')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('total_works')}
              >
                <div className="flex items-center space-x-1">
                  <span>Works</span>
                  {getSortIcon('total_works')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('total_estimate_cost')}
              >
                <div className="flex items-center space-x-1">
                  <span>Total Value</span>
                  {getSortIcon('total_estimate_cost')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status Breakdown
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('performance_score')}
              >
                <div className="flex items-center space-x-1">
                  <span>Performance</span>
                  {getSortIcon('performance_score')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Risk & Issues
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {districts.map((district) => {
              const performance = getPerformanceRating(
                district.financial_progress_percentage || 0,
                district.physical_progress_percentage || 0
              );
              const riskLevel = getRiskLevel(
                district.delayed_works || 0,
                district.total_works,
                district.budget_utilization_percentage || 0
              );

              return (
                <tr 
                  key={district.id} 
                  className="hover:bg-gray-50 transition-colors cursor-pointer group"
                  onClick={() => onDistrictClick(district)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {district.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {district.code} • {district.mandals_count || 0} Mandals
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">
                        {formatNumber(district.total_works)}
                      </div>
                      <div className="text-gray-500 text-xs">
                        <span className="text-green-600">{district.completed_works || 0} completed</span>
                        {(district.delayed_works || 0) > 0 && (
                          <span className="text-red-600 ml-2">{district.delayed_works} delayed</span>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(district.total_estimate_cost)}
                      </div>
                      <div className="text-gray-500 text-xs">
                        Spent: {formatCurrency(district.total_expenditure || 0)}
                      </div>
                      <div className="text-blue-600 text-xs">
                        {formatPercentage(district.budget_utilization_percentage || 0)} utilized
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Financial</span>
                        <span className="font-medium">
                          {formatPercentage(district.financial_progress_percentage || 0)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(district.financial_progress_percentage || 0, 100)}%` }}
                        ></div>
                      </div>
                      
                      {(district.total_length_sanctioned || 0) > 0 && (
                        <>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">Physical</span>
                            <span className="font-medium">
                              {formatPercentage(district.physical_progress_percentage || 0)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(district.physical_progress_percentage || 0, 100)}%` }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {(district.works_in_progress || 0) > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {district.works_in_progress} In Progress
                        </span>
                      )}
                      {(district.works_not_started || 0) > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {district.works_not_started} Not Started
                        </span>
                      )}
                      {(district.works_on_hold || 0) > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {district.works_on_hold} On Hold
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          performance.rating === 'Excellent' ? 'bg-green-500' :
                          performance.rating === 'Good' ? 'bg-blue-500' :
                          performance.rating === 'Average' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <span className={`text-sm font-medium ${performance.color}`}>
                          {performance.rating}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Score: {district.performance_score}/100
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          riskLevel === 'Low' ? 'bg-green-500' :
                          riskLevel === 'Medium' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <span className={`text-sm font-medium ${
                          riskLevel === 'Low' ? 'text-green-600' :
                          riskLevel === 'Medium' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {riskLevel} Risk
                        </span>
                      </div>
                      {(district.delayed_works || 0) > 0 && (
                        <div className="flex items-center text-xs text-red-600">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {district.delayed_works} Delayed
                        </div>
                      )}
                      {(district.pending_bills_amount || 0) > 0 && (
                        <div className="text-xs text-orange-600">
                          {formatCurrency(district.pending_bills_amount)} Pending
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDistrictClick(district);
                      }}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Mandals
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DistrictsTable;